import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Popconfirm, Row, Skeleton, Table, TableColumnsType, TableProps, Typography } from "antd";
import axios from "axios";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Case } from "@bindeleddet/core/types";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

const AdminCasebank = () => {
  const { Title } = Typography;
  const { width } = useViewport();

  const [cases, setCases] = useState<Case[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [companyNames, setCompanyNames] = useState<Record<number, string>>({});
  const [companyLoading, setCompanyLoading] = useState<boolean>(false);

  const history = useHistory();

  useEffect(() => {
    axios
      .get<Case[]>("/website-contents/cases/")
      .then(response => {
        setCases(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  useEffect(() => {
    const fetchCompanyNames = async () => {
      if (cases.length === 0 || Object.keys(companyNames).length > 0) return;

      setCompanyLoading(true);
      try {
        const companyPromises = cases.map(caseItem =>
          axios
            .get(`/companies/${caseItem.company}/`)
            .then(response => ({ id: caseItem.company, name: response.data.name }))
        );

        const results = await Promise.all(companyPromises);
        const newCompanyNames = results.reduce((acc, { id, name }) => {
          acc[id] = name;
          return acc;
        }, {} as Record<number, string>);

        setCompanyNames(newCompanyNames);
        setCompanyLoading(false);
      } catch {
        setCompanyLoading(false);
      }
    };

    fetchCompanyNames();
  }, [cases, companyNames]);

  const getFilteredCases = () =>
    cases.filter(caseItem => {
      if (searchTerm === "") {
        return caseItem;
      }
      if (caseItem.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return caseItem;
      }
      return false;
    });

  const filteredCases =
    cases &&
    getFilteredCases().map(caseItems => {
      return caseItems;
    });

  type OnChange = NonNullable<TableProps<Case>["onChange"]>;
  type GetSingle<T> = T extends (infer U)[] ? U : never;
  type Sorts = GetSingle<Parameters<OnChange>[2]>;

  const [sortedInfo, setSortedInfo] = useState<Sorts>({ columnKey: "created_at", order: "descend" });

  const handleChange: OnChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const columns: TableColumnsType<Case> = [
    {
      title: "Bedrift",
      key: "company",
      dataIndex: "company",
      render: id => {
        return companyLoading ? (
          <Skeleton.Button style={{ height: "1em", width: "100%" }} />
        ) : (
          companyNames[id] || "Ukjent bedrift"
        );
      },
    },
    {
      title: "Tittel",
      key: "title",
      dataIndex: "title",
      sorter: (a: Case, b: Case) => {
        return a.title.localeCompare(b.title);
      },
      sortOrder: sortedInfo.columnKey === "title" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Publiseringsdato",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: Case, b: Case) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA.getTime() - dateB.getTime();
      },
      sortOrder: sortedInfo.columnKey === "created_at" ? sortedInfo.order : null,
      defaultSortOrder: "descend",
      render: (text, record) => {
        const date = new Date(record.created_at);
        return date.toLocaleDateString();
      },
      ellipsis: true,
      width: 200,
    },
    {
      key: "edit",
      dataIndex: "id",
      render: id => (
        <EditOutlined
          style={{ fontSize: 20, cursor: "pointer" }}
          onClick={() => history.push(`/admin/resources/casebank/edit/${id}/`)}
        />
      ),
      width: 50,
    },
    {
      key: "x",
      render: (text, record) => {
        return (
          <Popconfirm
            key="delete-publication"
            title={`Slett "${record.title}"`}
            okText="Slett"
            placement="top"
            cancelText="Avbryt"
            style={{ backgroundColor: "var(--neutral-alt)" }}
            okType="danger"
            onConfirm={() => {
              axios
                .delete<Case>(`/website-contents/cases/${record.id}/`)
                .then(() => {
                  message("success", "Case slettet");
                  setCases(cases.filter(publication => publication.id !== record.id));
                })
                .catch(() => message("error", "Kunne ikke slette case. Prøv igjen senere."));
            }}
          >
            <DeleteOutlined style={{ color: "red", fontSize: 20, cursor: "pointer" }} />
          </Popconfirm>
        );
      },
      width: 50,
    },
  ];

  return (
    <>
      <Card style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Title level={2}>Casebank</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => history.push("/admin/resources/casebank/new/")}>
            Opprett ny case
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>

        {width > 768 ? (
          <Table columns={columns} dataSource={filteredCases} loading={loading} onChange={handleChange} />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Bedrift",
                dataIndex: "company",
                key: "company",
                render: company => {
                  return companyLoading ? (
                    <Skeleton.Button style={{ height: "1em", width: "100%" }} />
                  ) : (
                    companyNames[company as number] || "Ukjent bedrift"
                  );
                },
              },
              {
                title: "Tittel",
                dataIndex: "title",
                key: "title",
              },
              {
                title: "Beskrivelse",
                dataIndex: "description",
                key: "description",
              },
              {
                title: "Publiseringsdato",
                dataIndex: "created_at",
                key: "created_at",
                render: date => {
                  return new Date(date as string).toLocaleDateString("nb-NO", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                },
              },
            ]}
            onClick={id => history.push(`/admin/resources/casebank/edit/${id}/`)}
            data={filteredCases}
            loading={loading}
            emptyText="Ingen case funnet"
          />
        )}
      </Card>
    </>
  );
};

export default AdminCasebank;
